import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { adapter } from "@seek/tealium-adapter";
import { useTranslations } from "@vocab/react";
import { Box, Stack, Text, Strong, Badge, Link, Card } from "braid-design-system";
import { translations } from "../../../../../components/.vocab/index.mjs";
import { getMelwaysZone, getLanguage, buildUrlWithQueryParams } from "../../../BrandCard/BrandCardWithAssetLibrary.mjs";
import { companyLogoDimensions, companyLogoBackground, companyLogoDynamicSize, companyLogo, companyTitle, link, focusedCompanyProfileCard } from "./CompanyProfileCards.css.mjs";
const RatingIcon = () => /* @__PURE__ */ jsx(
  "svg",
  {
    width: "13",
    height: "12",
    viewBox: "0 0 13 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12.4972 4.37459C12.4431 4.15793 12.2806 4.04959 12.0639 3.99543L8.59723 3.50793L7.02639 0.366259C6.86389 0.0954256 6.53889 -0.0129077 6.26806 0.149592C6.15973 0.203759 6.10556 0.257926 6.05139 0.366259L4.48056 3.45376L1.06806 3.99543C0.743061 4.04959 0.526394 4.32043 0.580561 4.59126C0.580561 4.69959 0.634728 4.80793 0.743061 4.91626L3.23473 7.35376L2.63889 10.8204C2.58473 11.0913 2.80139 11.4163 3.07223 11.4704C3.18056 11.4704 3.28889 11.4704 3.39723 11.4163L6.48473 9.79126L9.57223 11.4163C9.84306 11.5788 10.1681 11.4704 10.2764 11.1996C10.3306 11.0913 10.3306 10.9829 10.3306 10.8746L9.73473 7.40792L12.2264 4.97043C12.4972 4.75376 12.5514 4.53709 12.4972 4.37459Z",
        fill: "#F57D02"
      }
    )
  }
);
const tealium = adapter();
const CompanyProfileCard = ({
  company,
  showReviews,
  showJobs,
  analyticsQueryParams,
  carouselCardPosition,
  onReviewsLandingPage,
  tabIndex,
  payload,
  featureFlags = {}
}) => {
  const { t } = useTranslations(translations);
  const jobCountNumber = Number(company.jobCount);
  const isSingleJob = jobCountNumber === 1;
  const showJobsBadge = showJobs && jobCountNumber >= 1;
  const cardContentsId = `company_title_${carouselCardPosition}`;
  const melwaysZone = getMelwaysZone(payload == null ? void 0 : payload.zone);
  const language = getLanguage({
    language: payload == null ? void 0 : payload.siteLanguage,
    melwaysZone
  });
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        display: "flex",
        flexDirection: "column",
        justifyContent: "spaceBetween",
        height: "full",
        id: cardContentsId,
        children: [
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsx(
              Box,
              {
                display: "flex",
                alignItems: "center",
                borderRadius: "standard",
                overflow: "hidden",
                className: [
                  companyLogoDimensions,
                  companyLogoBackground,
                  {
                    [companyLogoDynamicSize]: featureFlags.homepageLayoutRefresh
                  }
                ],
                children: /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "img",
                    src: company.logo,
                    className: companyLogo,
                    alt: "",
                    loading: "lazy"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx(Box, { className: companyTitle, children: /* @__PURE__ */ jsx(Text, { weight: "medium", maxLines: 2, children: company.title }) })
          ] }),
          showReviews || showJobsBadge ? /* @__PURE__ */ jsx(Box, { paddingTop: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            showReviews ? /* @__PURE__ */ jsxs(Text, { size: "small", children: [
              /* @__PURE__ */ jsx(RatingIcon, {}),
              " ",
              company.reviews.rating,
              " ",
              /* @__PURE__ */ jsx(Strong, { children: "·" }),
              " ",
              company.reviews.count,
              " ",
              t("Reviews")
            ] }) : null,
            showJobsBadge ? /* @__PURE__ */ jsx(Badge, { tone: onReviewsLandingPage ? "info" : "neutral", children: `${company.jobCount} ${isSingleJob ? t("Job") : t("Jobs")}` }) : null
          ] }) }) : null
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Link,
      {
        className: link,
        tabIndex,
        href: buildUrlWithQueryParams({
          relativeUrl: `/companies/${company.companyNameSlug}`,
          analyticsQueryParams,
          language,
          zone: melwaysZone
        }),
        "aria-labelledby": cardContentsId,
        onClick: async () => {
          try {
            tealium.link("company_profile_carousel_card_pressed", {
              companyName: company.title.toLowerCase(),
              companyId: company.id,
              brand: payload == null ? void 0 : payload.brand,
              currentPage: payload == null ? void 0 : payload.currentPage,
              isLoggedIn: payload == null ? void 0 : payload.isLoggedIn,
              siteCountry: payload == null ? void 0 : payload.siteCountry.toLowerCase(),
              siteLanguage: payload == null ? void 0 : payload.siteLanguage,
              loginId: payload == null ? void 0 : payload.loginId,
              zone: payload == null ? void 0 : payload.zone,
              experiments: payload == null ? void 0 : payload.experiments,
              siteSection: payload == null ? void 0 : payload.siteSection,
              cardPosition: carouselCardPosition,
              cardType: payload == null ? void 0 : payload.cardType,
              companyViewOrigin: payload == null ? void 0 : payload.companyViewOrigin
            });
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (e) {
            console.error({
              message: "Company profile carousel tealium.link() error",
              error: e
            });
          }
        }
      }
    )
  ] });
  if (featureFlags.homepageLayoutRefresh) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        component: "article",
        borderRadius: "large",
        height: "full",
        padding: "medium",
        boxShadow: "borderNeutralLight",
        background: "surface",
        className: focusedCompanyProfileCard,
        children: content
      }
    );
  }
  return /* @__PURE__ */ jsx(Card, { component: "article", rounded: true, height: "full", children: content });
};
export {
  CompanyProfileCard
};
